//@flow
import React, {useState} from 'react';
import {Grid, Hidden, Paper, Typography, makeStyles} from '@material-ui/core';

import CustomButton from '../../../../elements/CustomButton/CustomButton';
import {Add} from '@material-ui/icons';
import QuickReply from './QuickReply';
import RepliesForm from './RepliesForm';
import {useQuickReplies, useQuickRepliesActions} from '../utils';
import {colors} from '../../../../assets/styles/colors';
import {getPaginationCount} from '../../../../utils/pagination';
import Pagination from '@material-ui/lab/Pagination';

import type {QuickReply as QuickReplyType} from '../../../../entities';
import {useIsFeatureInTier} from '../../../../utils/component';
import {useTierRestrictionActions} from '../../../TierRestriction/utils';
import UpgradeIcon from '../../../../elements/icons/UpgradeIcon';

const useStyles = makeStyles(theme => ({
  root: {
    height: '100%',
    minWidth: '100%',
    minHeight: 'calc(100vh - 200px)',
    padding: '1rem 0',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  headerRow: {
    paddingBottom: '1rem',
  },
  container: {
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16);',
    borderRadius: '12px',
    padding: '1rem',
    height: '100%',
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  headerLabel: {
    fontWeight: '600',
    fontSize: '.85rem',
  },
  options: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '.5rem',
  },
  addButton: {
    padding: '.25rem 1rem',
  },
  buttonLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
    fontSize: '.85rem',
  },
  emptyContent: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: '1rem',
    maxWidth: '450px',
    margin: 'auto',
  },
  emptyText: {
    color: colors.grey700,
    fontWeight: 600,
    fontSize: '1.5rem',
    lineHeight: '1.25',
  },
  addContent: {
    padding: '.5rem 2rem',
    fontSize: '1rem',
    borderRadius: '12px',
  },
  pagination: {
    width: '100%',
    marginTop: '1rem',
    display: 'flex',
    justifyContent: 'center',
  },
  upgradeButton: {
    backgroundColor: 'white',
    color: colors.purple3,
    fontWeight: 600,
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    '&:hover': {
      backgroundColor: colors.grey200,
    },
  },
  upgradeButtonLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
    justifyContent: 'center',
  },
}));

export default function QuickReplies() {
  const classes = useStyles();

  const {quickReplies, loading, pagination} = useQuickReplies();
  const {
    deleteQuickReply,
    changeQuickReplyPagination,
  } = useQuickRepliesActions();

  const [replyToEdit, setReplyToEdit] = useState<
    QuickReplyType | typeof undefined
  >();

  const haQuickRepliesFeature = useIsFeatureInTier('TIENDACHAT_QUICK_REPLIES');

  const {showTierRestrictionModal} = useTierRestrictionActions();

  const [formOpen, setFormOpen] = useState(false);
  const closeForm = () => setFormOpen(false);
  const openForm = () => {
    if (!haQuickRepliesFeature) {
      showTierRestrictionModal();
      return;
    }
    setFormOpen(true);
  };

  return (
    <div className={classes.root}>
      {!haQuickRepliesFeature && (
        <CustomButton className={classes.upgradeButton} to="/tiers-list">
          <div className={classes.whiteButtonLabel}>
            <span>Mejora tu plan para acceder a esta funcionalidad</span>
            <UpgradeIcon width={18} height={18} />
          </div>
        </CustomButton>
      )}
      {quickReplies.length > 0 && (
        <Paper className={classes.container}>
          <Hidden xsDown>
            <Grid container alignItems="center" className={classes.headerRow}>
              <Grid item xs={2}>
                <Typography className={classes.headerLabel}>
                  ID/Nombre
                </Typography>
              </Grid>
              <Grid item xs={10} justify="space-between">
                <div className={classes.options}>
                  <Typography className={classes.headerLabel}>
                    Mensaje
                  </Typography>
                  <CustomButton
                    className={classes.addButton}
                    variant="contained"
                    disabled={!haQuickRepliesFeature}
                    onClick={openForm}
                  >
                    <span className={classes.buttonLabel}>
                      <Add fontSize="small" className="icon" />
                      <span>Crear template</span>
                    </span>
                  </CustomButton>
                </div>
              </Grid>
            </Grid>
          </Hidden>

          <Grid container spacing={2}>
            {quickReplies.map(reply => (
              <Grid item xs={12} key={reply.id}>
                <QuickReply
                  reply={reply}
                  onDeleted={() => {
                    deleteQuickReply(reply.id);
                  }}
                  onEdited={() => {
                    setReplyToEdit(reply);
                    // openForm();
                  }}
                />
              </Grid>
            ))}
            <Pagination
              count={getPaginationCount(pagination)}
              page={pagination.page + 1}
              className={classes.pagination}
              color="primary"
              onChange={(e, page) => changeQuickReplyPagination(page)}
            />
          </Grid>
        </Paper>
      )}
      {quickReplies.length === 0 && !loading && (
        <Paper className={classes.container}>
          <div className={classes.emptyContent}>
            <Typography
              variant="h6"
              align="center"
              className={classes.emptyText}
            >
              No hay respuestas rápidas
            </Typography>

            <Typography className={classes.headLine} align="center">
              Acelera tus flujos de conversación con respuestas rápidas
            </Typography>

            <CustomButton
              variant="contained"
              onClick={openForm}
              disabled={!haQuickRepliesFeature}
              className={classes.addContent}
            >
              <span className={classes.buttonLabel}>
                <Add fontSize="small" className="icon" />
                <span>Crear Respuesta</span>
              </span>
            </CustomButton>
          </div>
        </Paper>
      )}
      {(formOpen || replyToEdit) && (
        <RepliesForm
          onClose={() => {
            if (formOpen) closeForm();
            if (replyToEdit) setReplyToEdit(undefined);
          }}
          quickReply={replyToEdit}
        />
      )}
    </div>
  );
}
